import { typedMemo } from '@utils';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { BreadcrumbContext, BreadcrumbSegment } from './breadcrumbHooks';

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export const BreadcrumbProvider = typedMemo(
  ({ children }: Readonly<BreadcrumbProviderProps>) => {
    const [segments, setSegments] = useState<BreadcrumbSegment[]>([]);

    const registerSegment = useCallback((segment: BreadcrumbSegment) => {
      setSegments((prev) => {
        const filtered = prev.filter((s) => s.id !== segment.id);
        return [...filtered, segment].sort((a, b) => a.order - b.order);
      });
    }, []);

    const unregisterSegment = useCallback((id: string) => {
      setSegments((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const updateSegment = useCallback(
      (id: string, updates: Partial<BreadcrumbSegment>) => {
        setSegments((prev) =>
          prev
            .map((s) => (s.id === id ? { ...s, ...updates } : s))
            .sort((a, b) => a.order - b.order),
        );
      },
      [],
    );

    const contextValue = useMemo(
      () => ({ segments, registerSegment, unregisterSegment, updateSegment }),
      [segments, registerSegment, unregisterSegment, updateSegment],
    );

    return (
      <BreadcrumbContext.Provider value={contextValue}>
        {children}
      </BreadcrumbContext.Provider>
    );
  },
);
